import type { FormikValues } from "formik";
import { useFormikContext } from "formik";
import { set } from "lodash-es";
import { useLayoutEffect, useRef } from "react";

import type { Paths } from "@citrine/utils/types";

interface SyncProps<Shape extends FormikValues, ValueType> {
  name: Paths<Shape, ValueType>;
  value: ValueType;
  shouldValidate?: boolean;
}

/**
 * The purpose of this hook is to watch a value and keep it synchronized
 * within the formik bag
 *
 * This allows you to specify a single field as being read-only and make its
 * source value available in the form tree using formik's context
 */
function useSyncField<Shape extends FormikValues, ValueType>({
  name,
  value,
  shouldValidate,
}: SyncProps<Shape, ValueType>) {
  const formikContext = useFormikContext<Shape>();

  const formikRef = useRef(formikContext);
  formikRef.current = formikContext;

  useLayoutEffect(() => {
    // if the form is not dirty, don't put it in a dirty state because of this synced value
    if (formikRef.current.dirty) {
      formikRef.current.setFieldValue(name, value, shouldValidate);
    } else {
      formikRef.current.resetForm({
        values: set(formikRef.current.values, name, value),
      });
    }
  }, [name, value, shouldValidate]);
}

export function SyncField<Shape extends FormikValues, ValueType>(
  props: SyncProps<Shape, ValueType>,
) {
  useSyncField<Shape, ValueType>(props);

  return null;
}
